import React, { useState, useMemo } from "react";
import { Avatar } from "@fluentui/react-components";
import HoverCard from "../../../components/hoverCard/HoverCard";
import IconInput from "../../../components/controls/input/iconInput/IconInput";
import { SearchIcon } from "../../../modules/default-svg";
import { getAccountCurrency, displayDecimalFormatted } from "../../../utils/systemSettings";
import styles from "../ResourcesReport.module.scss";

// Maps column config keys → the data field on each row
const COL_DATA_MAP = {
  capacityHours: { dataKey: "capacityHours", type: "hours" },
  allocationHours: { dataKey: "allocationHours", type: "hours" },
  utilization: { dataKey: "utilizationPercentage", type: "pct" },
  loggedHours: { dataKey: "loggedHours", type: "hours" },
  allocCost: { dataKey: "allocationCost", type: "currency" },
  allocBill: { dataKey: "allocationBill", type: "currency" },
  loggedCost: { dataKey: "loggedCost", type: "currency" },
  loggedBill: { dataKey: "loggedBill", type: "currency" },
  loggedUtilization: { dataKey: "loggedUtilizationPercentage", type: "pct" },
  allocGp: { dataKey: "_allocGp", type: "currency" },
  loggedGp: { dataKey: "_loggedGp", type: "currency" },
  costVariance: { dataKey: "_costVariance", type: "currency" },
  billVariance: { dataKey: "_billVariance", type: "currency" },
};

function getUtilizationClass(pct) {
  if (pct >= 100) return styles.utilizationHigh;
  if (pct >= 50) return styles.utilizationMid;
  return styles.utilizationLow;
}

export default function ReportTable({ items, settings, chartsVisible, columns }) {
  const currency = getAccountCurrency(settings);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [expandedResources, setExpandedResources] = useState({});
  const [expandedProjects, setExpandedProjects] = useState({});
  const [expandedScopes, setExpandedScopes] = useState({});

  const fmt = (val) => displayDecimalFormatted(settings, val ?? 0);
  const fmtCurrency = (val) => `${currency} ${fmt(val)}`;
  const fmtHours = (val) => `${displayDecimalFormatted(settings, Number((val ?? 0).toFixed(2)))}h`;
  const fmtPct = (val) => `${displayDecimalFormatted(settings,Number((val ?? 0).toFixed(2)))}%`;

  // Visible data columns (excluding the fixed "resources" / name column)
  const visibleCols = useMemo(
    () => columns.filter((c) => c.key !== "resources" && c.isVisible),
    [columns]
  );

  // Variance helpers (positive costVariance = overrun, negative billVariance = shortfall)
  const isCostVarianceOverrun = (row) => ((row.loggedCost ?? 0) - (row.allocationCost ?? 0)) > 0;
  const isBillVarianceOverrun = (row) => ((row.loggedBill ?? 0) - (row.allocationBill ?? 0)) < 0;

  const getVarianceBgClass = (colKey, row) => {
    if (colKey === "costVariance" && isCostVarianceOverrun(row)) return styles.varianceOverrunBg;
    if (colKey === "billVariance" && isBillVarianceOverrun(row)) return styles.varianceOverrunBg;
    return "";
  };

  // Enrich a row with computed variance fields
  const addVariance = (row) => ({
    ...row,
    _allocGp: row.allocationGp ?? ((row.allocationBill ?? 0) - (row.allocationCost ?? 0)),
    _loggedGp: row.loggedGp ?? ((row.loggedBill ?? 0) - (row.loggedCost ?? 0)),
    _costVariance: (row.loggedCost ?? 0) - (row.allocationCost ?? 0),
    _billVariance: (row.loggedBill ?? 0) - (row.allocationBill ?? 0),
  });

  // Render a data cell for a given column key
  const renderCell = (colKey, row, isDim) => {
    if (isDim) return <td key={colKey} className={styles.dimCell}>–</td>;
    const meta = COL_DATA_MAP[colKey];
    if (!meta) return <td key={colKey}>–</td>;
    const val = row[meta.dataKey];
    const bgClass = getVarianceBgClass(colKey, row);

    if (colKey === "utilization" || colKey === "loggedUtilization") {
      return (
        <td key={colKey}>
          <span className={`${styles.utilizationBadge} ${getUtilizationClass(val)}`}>
            {fmtPct(val)}
          </span>
        </td>
      );
    }
    const formatted =
      meta.type === "hours" ? fmtHours(val) :
      meta.type === "pct" ? fmtPct(val) :
      fmtCurrency(val);

    return <td key={colKey} className={bgClass}>{formatted}</td>;
  };

  // Filter
  const filtered = useMemo(() => {
    if (!searchTerm) return items;
    const q = searchTerm.toLowerCase();
    return items.filter((r) => r.resource?.name?.toLowerCase().includes(q));
  }, [items, searchTerm]);

  // Sort
  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    const arr = [...filtered];
    arr.sort((a, b) => {
      let va, vb;
      if (sortKey === "name") {
        va = a.resource?.name?.toLowerCase() || "";
        vb = b.resource?.name?.toLowerCase() || "";
      } else {
        const meta = COL_DATA_MAP[sortKey];
        const dk = meta?.dataKey || sortKey;
        // For computed fields, calculate on the fly
        if (dk === "_costVariance") {
          va = (a.loggedCost ?? 0) - (a.allocationCost ?? 0);
          vb = (b.loggedCost ?? 0) - (b.allocationCost ?? 0);
        } else if (dk === "_billVariance") {
          va = (a.loggedBill ?? 0) - (a.allocationBill ?? 0);
          vb = (b.loggedBill ?? 0) - (b.allocationBill ?? 0);
        } else if (dk === "_allocGp") {
          va = (a.allocationGp ?? ((a.allocationBill ?? 0) - (a.allocationCost ?? 0)));
          vb = (b.allocationGp ?? ((b.allocationBill ?? 0) - (b.allocationCost ?? 0)));
        } else if (dk === "_loggedGp") {
          va = (a.loggedGp ?? ((a.loggedBill ?? 0) - (a.loggedCost ?? 0)));
          vb = (b.loggedGp ?? ((b.loggedBill ?? 0) - (b.loggedCost ?? 0)));
        } else {
          va = a[dk] ?? 0;
          vb = b[dk] ?? 0;
        }
      }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const toggleResource = (id) => setExpandedResources((p) => ({ ...p, [id]: !p[id] }));
  const toggleProject = (resId, projId) => {
    const key = `${resId}_${projId}`;
    setExpandedProjects((p) => ({ ...p, [key]: !p[key] }));
  };
  const toggleScope = (resId, projId, scopeId) => {
    const key = `${resId}_${projId}_${scopeId}`;
    setExpandedScopes((p) => ({ ...p, [key]: !p[key] }));
  };

  const renderSortIcon = (key) => {
    if (sortKey !== key) return <span className={styles.sortIcon}>⇅</span>;
    return <span className={`${styles.sortIcon} ${styles.sortIconActive}`}>{sortDir === "asc" ? "↑" : "↓"}</span>;
  };

  // Scope & task dim columns — these levels don't have all fields
  const SCOPE_DIM_KEYS = new Set(["capacityHours", "utilization", "loggedUtilization", "loggedHours", "loggedCost", "loggedBill", "loggedGp", "costVariance", "billVariance"]);
  const TASK_DIM_KEYS = new Set(["capacityHours", "utilization", "loggedUtilization", "loggedHours", "loggedCost", "loggedBill", "loggedGp", "costVariance", "billVariance"]);

  return (
    <div className={styles.tableSection}>
      <div className={styles.tableHeader}>
        <IconInput icon={<SearchIcon />} placeholder="Search by resource name" handleChange={setSearchTerm} />
      </div>

      <div className={`${styles.tableWrapper} ${!chartsVisible ? styles.tableWrapperExpanded : ""}`}>
        <table className={styles.reportTable}>
          <thead>
            <tr>
              <th onClick={() => handleSort("name")}>
                Resource {renderSortIcon("name")}
              </th>
              {visibleCols.map((col) => (
                <th key={col.key} onClick={() => handleSort(col.key)}>
                  {col.customName || col.name} {renderSortIcon(col.key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((item) => {
              const res = item.resource;
              const resExpanded = expandedResources[res.id];
              const resRow = addVariance(item);

              return (
                <React.Fragment key={res.id}>
                  {/* ── Resource Row ───────────────────────────────── */}
                  <tr className={styles.resourceRow} onClick={() => toggleResource(res.id)}>
                    <td>
                      <div className={styles.resourceCell}>
                        <span className={`${styles.expandIcon} ${resExpanded ? styles.expandIconOpen : ""}`}>▶</span>
                        <HoverCard
                          HoverCardTrigger={
                            <Avatar color="colorful" size={28} name={res.name} image={{ src: res.image }} />
                          }
                          AvatarId={res.id}
                          AvatarName={res.name}
                          AvatarImage={res.image}
                          AvatarDesignation={res.title || ""}
                          AvatarEmail={res.email}
                          AvatarPhone={res.phoneNumber}
                          AvatarIsExternal={res.isExternal}
                          CanSendMessage={true}
                        />
                        <div className={styles.resourceInfo}>
                          <span className={styles.resourceNameText}>{res.name}</span>
                          {res.title && <span className={styles.resourceTitleText}>{res.title}</span>}
                        </div>
                      </div>
                    </td>
                    {visibleCols.map((col) => renderCell(col.key, resRow, false))}
                  </tr>

                  {/* ── Project Rows ──────────────────────────────── */}
                  {resExpanded &&
                    item.projects?.map((proj) => {
                      const projKey = `${res.id}_${proj.project.id}`;
                      const projExpanded = expandedProjects[projKey];
                      const projRow = addVariance(proj);

                      return (
                        <React.Fragment key={projKey}>
                          <tr className={styles.projectRow} onClick={() => toggleProject(res.id, proj.project.id)}>
                            <td>
                              <div className={styles.nestedName}>
                                <span className={`${styles.expandIcon} ${projExpanded ? styles.expandIconOpen : ""}`}>▶</span>
                                <span>{proj.project.name}</span>
                                {proj.project.refId && <span className={styles.refId}>({proj.project.refId})</span>}
                              </div>
                            </td>
                            {visibleCols.map((col) => renderCell(col.key, projRow, false))}
                          </tr>

                          {/* ── Scope Rows ──────────────────────────── */}
                          {projExpanded &&
                            proj.scopes?.map((scope) => {
                              const scopeKey = `${res.id}_${proj.project.id}_${scope.scope.id}`;
                              const scopeExpanded = expandedScopes[scopeKey];
                              const scopeRow = addVariance(scope);

                              return (
                                <React.Fragment key={scopeKey}>
                                  <tr className={styles.scopeRow} onClick={() => toggleScope(res.id, proj.project.id, scope.scope.id)}>
                                    <td>
                                      <div className={styles.nestedName}>
                                        <span className={`${styles.expandIcon} ${scopeExpanded ? styles.expandIconOpen : ""}`}>▶</span>
                                        <span>{scope.scope.name}</span>
                                        {scope.scope.refId && <span className={styles.refId}>({scope.scope.refId})</span>}
                                      </div>
                                    </td>
                                    {visibleCols.map((col) => renderCell(col.key, scopeRow, SCOPE_DIM_KEYS.has(col.key)))}
                                  </tr>

                                  {/* ── Task Rows ────────────────────── */}
                                  {scopeExpanded &&
                                    scope.tasks?.map((task) => {
                                      const taskRow = addVariance(task);
                                      return (
                                        <tr key={`${scopeKey}_${task.task.id}`} className={styles.taskRow}>
                                          <td>
                                            <div className={styles.nestedName}>
                                              <span style={{ width: 18 }} />
                                              <span>{task.task.name}</span>
                                              {task.task.refId && <span className={styles.refId}>({task.task.refId})</span>}
                                            </div>
                                          </td>
                                          {visibleCols.map((col) => renderCell(col.key, taskRow, TASK_DIM_KEYS.has(col.key)))}
                                        </tr>
                                      );
                                    })}
                                </React.Fragment>
                              );
                            })}
                        </React.Fragment>
                      );
                    })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
